/**
 * Decides whether the dashboard should offer the live seat-map "Purchase
 * Seats" flow for a booking. The flow is strictly once-per-booking: the
 * moment flight_data.seat_assignments has any seat, eligibility flips off
 * (the server enforces the same rule with a 409).
 *
 * Mirrors the server's SEATMAP_STATIC_SKIP_CARRIERS list — these airlines are
 * sold via GDS on Duffel and never return seat maps, so offering the flow
 * would just waste the customer's time. For them (and any other ineligible
 * case) the dashboard falls back to the existing concierge SMS path.
 */
import { getMarketingFareName, isBasePremiumFareBrand } from './marketingFareName.js';

export const SEATMAP_UNSUPPORTED_CARRIERS = new Set(['DL', 'WN', 'B6', 'AS', 'F9', 'G4', 'NK']);

/** Customer-facing names for the unsupported set — interpolated into Tommy's
 * knowledge base so the prompt can never drift from this list. */
export const SEATMAP_UNSUPPORTED_AIRLINE_NAMES = ['Delta', 'Southwest', 'JetBlue', 'Alaska', 'Frontier', 'Allegiant', 'Spirit'];

// Booking flight_data isn't uniform: some rows have marketing_carrier.iata_code,
// some only the airline display name, and some embed the code in the flight
// number string ("AA 1234"). Resolve through all three before declaring a
// segment unusable.
const CARRIER_NAME_TO_IATA = {
  'american airlines': 'AA', american: 'AA',
  'delta air lines': 'DL', delta: 'DL',
  'united airlines': 'UA', united: 'UA',
  'southwest airlines': 'WN', southwest: 'WN',
  'jetblue airways': 'B6', jetblue: 'B6',
  'alaska airlines': 'AS', alaska: 'AS',
  'spirit airlines': 'NK', spirit: 'NK',
  'frontier airlines': 'F9', frontier: 'F9',
  'allegiant air': 'G4', allegiant: 'G4',
  'hawaiian airlines': 'HA', hawaiian: 'HA',
  'sun country airlines': 'SY', 'sun country': 'SY',
};

export function resolveSegmentCarrier(segment) {
  const code = segment?.marketing_carrier?.iata_code;
  if (code) return String(code).toUpperCase().trim();
  const rawNumber = String(segment?.marketing_carrier_flight_number || '').trim();
  const prefix = (rawNumber.match(/^([A-Z][A-Z0-9])\s*\d/i) || [])[1];
  if (prefix) return prefix.toUpperCase();
  const name = String(segment?.marketing_carrier?.name || '').toLowerCase().trim();
  return CARRIER_NAME_TO_IATA[name] || null;
}

export function getSeatPurchaseEligibility(booking) {
  const flightData = booking?.flightData || booking?.flight_data || {};

  const status = String(booking?.status || '').toLowerCase();
  if (status === 'cancelled' || status === 'canceled') {
    return { eligible: false, reason: 'cancelled' };
  }

  /** Basic Economy fares don't sell advance seat assignments, so the live
   *  map re-search comes back empty and the flow dead-ends into the concierge
   *  fallback AFTER promising a cabin layout. Gate it statically so the
   *  dashboard (and Tommy's TRIP CAPABILITIES) route straight to the
   *  concierge path instead. 4RA5XU was the canary: WN+AA Basic Economy,
   *  capabilities said "the map WILL open", the flow handed the customer to
   *  chat, and the concierge told them to try the map again. */
  const fareName = getMarketingFareName(booking);
  if (String(fareName || '').toLowerCase().includes('basic')) {
    return { eligible: false, reason: 'basic_economy_fare' };
  }

  /** A premium cabin's base tier (United Base Business) charges for the
   *  seats its full tier includes. TS2MFP was handed lie-flat seats at $0 at
   *  checkout: its option had no brand, so the seat lookup could use any
   *  fare's map. Route the base tier the way Basic Economy goes, to the team,
   *  who book seats at the airline's real price. */
  if (isBasePremiumFareBrand(fareName)) {
    return { eligible: false, reason: 'base_premium_fare' };
  }

  const assignments = flightData.seat_assignments || {};
  const hasSeats = Object.values(assignments).some(
    (segments) => segments && typeof segments === 'object' && Object.keys(segments).length > 0
  );
  if (hasSeats) return { eligible: false, reason: 'already_assigned' };

  const slices = Array.isArray(flightData.slices) ? flightData.slices : [];
  const futureSlices = slices.filter((slice) => {
    const departingAt = slice?.segments?.[0]?.departing_at;
    if (!departingAt) return false;
    const ts = new Date(departingAt).getTime();
    return Number.isFinite(ts) && ts > Date.now();
  });
  if (futureSlices.length === 0) return { eligible: false, reason: 'no_upcoming_flights' };

  // The Duffel re-search needs the full marketing carrier + flight number chain.
  const carriers = new Set();
  let allSegmentsHaveNumbers = true;
  futureSlices.forEach((slice) => {
    (slice.segments || []).forEach((segment) => {
      const carrier = resolveSegmentCarrier(segment);
      const hasNumber = /\d/.test(String(segment?.marketing_carrier_flight_number || ''));
      if (carrier) carriers.add(carrier);
      if (!carrier || !hasNumber) allSegmentsHaveNumbers = false;
    });
  });
  if (!allSegmentsHaveNumbers || carriers.size === 0) {
    return { eligible: false, reason: 'missing_flight_numbers' };
  }

  const anySupported = [...carriers].some((c) => !SEATMAP_UNSUPPORTED_CARRIERS.has(c));
  if (!anySupported) return { eligible: false, reason: 'carrier_unsupported' };

  return { eligible: true, futureSlices };
}
